// ═══════════════════════════════════════════════════════════════
// Perimeter Module — HERE API + Overpass Utilities
// POI scanning + geocoding via HERE (through Worker proxy)
// Road intersections via Overpass (no HERE equivalent)
// ═══════════════════════════════════════════════════════════════

import { MILES_TO_METERS, POI_CATEGORIES, DIR_ORDER, type POICategory } from './perimeterConstants';
import { haversine, getCardinalDirection, type LatLng, type PerimeterPOI, type PerimeterClosure } from './perimeterGeo';
const WORKER = '/api/worker';
const H = { 'Accept': 'application/json' };

// ── Types ────────────────────────────────────────────────────

interface HEREItem {
  id: string;
  title: string;
  position: { lat: number; lng: number };
  address?: { label?: string; street?: string; houseNumber?: string };
  contacts?: Array<{ phone?: Array<{ value: string }> }>;
  distance?: number;
  categories?: Array<{ id: string; name: string }>;
}

interface HEREBrowseResponse {
  items: HEREItem[];
}

interface HEREGeocodeItem {
  title: string;
  id: string;
  position: { lat: number; lng: number };
  address?: { label?: string };
  scoring?: { queryScore: number };
}

interface OverpassElement {
  type: 'node' | 'way' | 'relation';
  id: number;
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: Record<string, string>;
  nodes?: number[];
}

interface OverpassResponse {
  elements: OverpassElement[];
}

type ProgressCallback = (message: string) => void;

// ── HERE Browse (POI scan) ───────────────────────────────────

async function hereBrowse(
  lat: number,
  lng: number,
  radiusM: number,
  categories: string,
  limit = 100,
): Promise<HEREBrowseResponse> {
  const params = new URLSearchParams({
    at: `${lat},${lng}`,
    in: `circle:${lat},${lng};r=${radiusM}`,
    categories,
    limit: String(limit),
  });
  const r = await fetch(`${WORKER}/api/here/browse?${params}`, { headers: H });
  if (!r.ok) throw new Error(`HERE Browse ${r.status}`);
  return r.json();
}

// ── Overpass Query (for intersections only) ──────────────────

const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
];

export async function queryOverpass(query: string, retries = 2): Promise<OverpassResponse> {
  let lastErr: Error | null = null;

  for (const endpoint of OVERPASS_ENDPOINTS) {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const r = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'data=' + encodeURIComponent(query),
        });

        if (r.status === 429) {
          await new Promise(ok => setTimeout(ok, 3000 * (attempt + 1)));
          continue;
        }
        if (r.status === 504) {
          await new Promise(ok => setTimeout(ok, 2000 * (attempt + 1)));
          continue;
        }
        if (!r.ok) throw new Error('Overpass ' + r.status);

        return r.json();
      } catch (e) {
        lastErr = e as Error;
        if (attempt === retries) break;
      }
    }
  }

  throw lastErr || new Error('All Overpass endpoints failed');
}

// ── Scan POIs (HERE Browse API) ──────────────────────────────

export async function scanPOIs(
  center: LatLng,
  radius: number,
  onProgress?: ProgressCallback
): Promise<PerimeterPOI[]> {
  const { lat, lng } = center;
  const radiusM = Math.round(radius * MILES_TO_METERS);

  onProgress?.('Scanning POIs via HERE API...');

  // Make ONE HERE Browse call per category, in parallel.
  // HERE Browse caps the result list at 100 items per call. Previously we
  // bundled all categories into a single call and HERE returned 100 nearest
  // items total — so a zone with lots of schools/hospitals/etc nearby would
  // crowd out distant gas stations and they'd never appear. With separate
  // calls each category gets its own 100-item budget.
  const requestCats = POI_CATEGORIES.filter(c => c.hereCategory);
  const responses = await Promise.all(
    requestCats.map(async c => {
      try {
        const data = await hereBrowse(lat, lng, radiusM, c.hereCategory!);
        return data.items || [];
      } catch {
        return [];
      }
    })
  );

  // Flatten + dedupe by HERE id (a POI can match multiple categories
  // and appear in more than one response)
  const seen = new Set<string>();
  const items: HEREItem[] = [];
  for (const list of responses) {
    for (const it of list) {
      if (!seen.has(it.id)) {
        seen.add(it.id);
        items.push(it);
      }
    }
  }

  onProgress?.('Processing POI results...');

  // Per-category matchers. Each entry has the POI key, the exact HERE
  // category ID we hardcoded, name keywords, and title keywords. We
  // iterate this list IN PRIORITY ORDER (most critical first) and the
  // first OUR-category that finds ANY HERE-category match wins.
  // This was previously a "first HERE category wins" loop, which meant
  // when HERE returned [Convenience Store, Fire Station] for a fire
  // station with a vending machine, the supermarket ID matched first
  // and fire stations got bucketed as groceries.
  interface Matcher {
    key: string;
    nameKeywords: string[];
    titleKeywords: string[];
  }
  const MATCHERS: Matcher[] = [
    // Highest priority — life safety
    { key: 'hospital',     nameKeywords: ['hospital', 'medical center'],
                           titleKeywords: ['hospital', 'medical center', 'medical ctr'] },
    { key: 'fire_station', nameKeywords: ['fire station', 'fire department'],
                           titleKeywords: ['fire station', 'fire department', 'fire dept', 'firehouse'] },
    { key: 'police',       nameKeywords: ['police', 'sheriff', 'law enforcement'],
                           titleKeywords: ['police', 'sheriff', 'pd ', ' pd', 'precinct'] },
    { key: 'clinic',       nameKeywords: ['clinic', 'urgent care', 'health care', 'health center'],
                           titleKeywords: ['clinic', 'urgent care'] },
    { key: 'shelter',      nameKeywords: ['shelter', 'emergency shelter'],
                           titleKeywords: [] },
    // Vulnerable populations
    { key: 'school',       nameKeywords: ['school', 'university', 'college', 'kindergarten', 'preschool', 'academy', 'high school', 'elementary', 'middle school'],
                           titleKeywords: ['school', 'academy', 'high school', 'elementary', 'middle school'] },
    { key: 'childcare',    nameKeywords: ['daycare', 'childcare', 'child care', 'nursery school', 'preschool'],
                           titleKeywords: ['daycare', 'childcare', 'kindercare'] },
    { key: 'nursing',      nameKeywords: ['nursing home', 'assisted living', 'senior care', 'retirement community'],
                           titleKeywords: ['nursing', 'assisted living', 'senior', 'retirement'] },
    // Often used as cooling centers, evacuation gathering points, and Wi-Fi access
    { key: 'library',      nameKeywords: ['library', 'public library'],
                           titleKeywords: ['library'] },
    { key: 'community_center', nameKeywords: ['community center', 'community centre', 'civic center', 'civic centre', 'recreation center'],
                           titleKeywords: ['community center', 'community ctr', 'civic center', 'civic ctr', 'recreation center', 'rec center'] },
    // Essential services
    { key: 'post_office',  nameKeywords: ['post office', 'postal'],
                           titleKeywords: ['post office', 'usps'] },
    { key: 'pharmacy',     nameKeywords: ['pharmacy', 'drugstore', 'drug store'],
                           titleKeywords: ['pharmacy', 'cvs', 'walgreens', 'rite aid'] },
    { key: 'fuel',         nameKeywords: ['gas station', 'fuel station', 'petrol station'],
                           titleKeywords: ['shell', 'chevron', 'exxon', 'bp ', 'mobil', 'sunoco', 'citgo', 'marathon', '76 ', 'arco', 'speedway', 'wawa', 'sheetz', 'racetrac', 'quiktrip'] },
    { key: 'government',   nameKeywords: ['government office', 'city hall', 'courthouse', 'town hall', 'municipal'],
                           titleKeywords: ['city hall', 'courthouse', 'town hall', 'municipal'] },
    { key: 'supermarket',  nameKeywords: ['supermarket', 'grocery store', 'grocery'],
                           titleKeywords: ['publix', 'kroger', 'whole foods', 'trader joe', 'walmart', 'target', 'aldi', 'wegmans', 'safeway', 'food lion', 'harris teeter', 'sprouts'] },
    // Worship is LAST and has the strictest matching — title or name must
    // explicitly mention a worship term. This stops gyms / health clubs
    // from getting bucketed here (they used to via the old prefix fallback).
    { key: 'worship',      nameKeywords: ['church', 'mosque', 'temple', 'synagogue', 'religious place', 'place of worship', 'cathedral', 'chapel'],
                           titleKeywords: ['church', 'mosque', 'temple', 'synagogue', 'cathedral', 'chapel', 'parish', 'congregation', 'ministry', 'fellowship', 'baptist', 'methodist', 'lutheran', 'presbyterian', 'catholic'] },
  ];

  const catByKey: Record<string, POICategory> = {};
  POI_CATEGORIES.forEach(c => { catByKey[c.key] = c; });

  // Title keyword vetoes — POIs whose title matches any of these are
  // dropped BEFORE any category matching, regardless of what HERE says.
  // The hereCategory IDs we hardcoded historically were guesses that turned
  // out to be wrong (e.g. 800-8600-0191 was supposed to be "Place of Worship"
  // but HERE actually uses it for fitness centers). Until we verify every
  // ID with real HERE API responses, we drop the noisy POI categories at
  // the title level.
  const FITNESS_VETO = ['gym', 'fitness', 'crossfit', 'pilates', 'yoga', 'health club', 'fight club', 'martial arts', 'jiu jitsu', 'jiujitsu', 'taekwondo', 'karate'];
  const RETAIL_VETO = ['vape', 'tobacco', 'smoke shop', 'tattoo', 'nail salon', 'hair salon', 'barber'];

  const pois: PerimeterPOI[] = [];

  items.forEach(item => {
    if (!item.position || !item.title) return;

    const itemLat = item.position.lat;
    const itemLng = item.position.lng;

    const ltitle = item.title.toLowerCase();
    const hereCats = item.categories || [];
    const hereCatNames = hereCats.map(c => (c.name || '').toLowerCase());

    // ── Pre-filter vetoes ────────────────────────────────────────
    // Drop fitness/gym POIs entirely. We do not have a fitness category
    // and HERE's category IDs in this space have been wrong historically,
    // bucketing them as worship.
    if (FITNESS_VETO.some(kw => ltitle.includes(kw))) return;
    // Drop noisy retail (vape shops, tattoo parlors, etc.) — not useful
    // for emergency response and prone to mis-bucketing.
    if (RETAIL_VETO.some(kw => ltitle.includes(kw))) return;
    // Also drop POIs whose ONLY HERE category name contains "fitness"
    // or "health club" (in case the title doesn't say gym but the category
    // is clearly fitness — e.g. "Equinox" or "Crunch").
    if (hereCatNames.length > 0 && hereCatNames.every(n =>
      n.includes('fitness') || n.includes('health club') || n.includes('gymnasium') || n.includes('sport')
    )) return;

    // Iterate OUR categories in priority order. First match wins.
    // We DO NOT use exact HERE category ID matching anymore — those IDs
    // were unreliable. Only HERE category NAME and POI TITLE keywords.
    let cat: POICategory | null = null;
    for (const matcher of MATCHERS) {
      const ourCat = catByKey[matcher.key];
      if (!ourCat) continue;

      // (a) HERE category name contains a keyword
      const nameHit = hereCatNames.some(n =>
        matcher.nameKeywords.some(kw => n.includes(kw))
      );
      if (nameHit) {
        cat = ourCat;
        break;
      }
      // (b) POI title contains a strong identifying keyword
      const titleHit = matcher.titleKeywords.some(kw => ltitle.includes(kw));
      if (titleHit) {
        cat = ourCat;
        break;
      }
    }

    if (!cat) return;

    // ── Final defense-in-depth veto for worship ──────────────────
    // Even if our matcher landed on worship, double-check that the title
    // OR a HERE category name actually mentions a worship-specific term.
    // Otherwise drop the POI rather than mislabel.
    if (cat.key === 'worship') {
      const worshipTerms = ['church', 'mosque', 'temple', 'synagogue', 'cathedral', 'chapel', 'parish', 'congregation', 'ministry', 'fellowship', 'baptist', 'methodist', 'lutheran', 'presbyterian', 'catholic', 'religious'];
      const titleSounds = worshipTerms.some(kw => ltitle.includes(kw));
      const nameSounds = hereCatNames.some(n =>
        worshipTerms.some(kw => n.includes(kw)) || n.includes('place of worship')
      );
      if (!titleSounds && !nameSounds) return;
    }

    const dist = haversine(lat, lng, itemLat, itemLng);
    if (dist > radius * 1.2) return;

    // Dedup within ~50m (0.035 mi)
    if (pois.some(p => haversine(p.lat, p.lng, itemLat, itemLng) < 0.035)) return;

    // Extract address
    const addr = item.address?.street
      ? `${item.address.houseNumber || ''} ${item.address.street}`.trim()
      : item.address?.label || '';

    // Extract phone
    let phone = '';
    if (item.contacts) {
      for (const c of item.contacts) {
        if (c.phone?.[0]?.value) { phone = c.phone[0].value; break; }
      }
    }

    pois.push({
      id: 'here-' + item.id,
      name: item.title,
      lat: itemLat,
      lng: itemLng,
      category: cat.key,
      categoryLabel: cat.label,
      categoryIcon: cat.icon,
      address: addr,
      phone,
      distance: dist,
      source: 'here',
    });
  });

  pois.sort((a, b) => a.distance - b.distance);
  return pois;
}

// ── Scan Intersections (Overpass — no HERE equivalent) ───────

export async function scanIntersections(
  center: LatLng,
  radius: number,
  onProgress?: ProgressCallback
): Promise<PerimeterClosure[]> {
  const { lat, lng } = center;
  const radiusM = Math.round(radius * MILES_TO_METERS);
  const band = Math.max(150, radiusM * 0.08);
  const outer = radiusM + band;

  onProgress?.('Querying road network...');

  const q = `[out:json][timeout:25];(way["highway"~"^(motorway|trunk|primary|secondary|tertiary|residential|unclassified)$"]["name"](around:${outer},${lat},${lng}););out body;>;out skel qt;`;

  let data: OverpassResponse;
  try {
    data = await queryOverpass(q);
  } catch {
    onProgress?.('Fallback: scanning major roads only...');
    data = await queryOverpass(
      `[out:json][timeout:15];(way["highway"~"^(primary|secondary|tertiary)$"]["name"](around:${outer},${lat},${lng}););out body;>;out skel qt;`
    );
  }

  onProgress?.('Processing road intersections...');

  // Build node and way maps
  const nodes: Record<number, { lat: number; lng: number }> = {};
  const ways: OverpassElement[] = [];

  (data.elements || []).forEach(el => {
    if (el.type === 'node' && el.lat != null && el.lon != null) {
      nodes[el.id] = { lat: el.lat, lng: el.lon };
    } else if (el.type === 'way' && el.tags?.name) {
      ways.push(el);
    }
  });

  // Find intersections within the perimeter band
  const intMap: Record<
    number,
    { streets: Set<string>; types: Set<string>; lat: number; lng: number; dist: number }
  > = {};

  ways.forEach(w => {
    const name = w.tags!.name!;
    const hwy = w.tags!.highway!;
    (w.nodes || []).forEach(nid => {
      const n = nodes[nid];
      if (!n) return;
      const dist = haversine(lat, lng, n.lat, n.lng);
      const distM = dist * MILES_TO_METERS;
      if (Math.abs(distM - radiusM) < band) {
        if (!intMap[nid]) {
          intMap[nid] = { streets: new Set(), types: new Set(), lat: n.lat, lng: n.lng, dist };
        }
        intMap[nid].streets.add(name);
        intMap[nid].types.add(hwy);
      }
    });
  });

  // Build closure list — only include actual intersections (2+ streets)
  const closures: PerimeterClosure[] = [];

  Object.entries(intMap).forEach(([nid, info]) => {
    const streets = Array.from(info.streets);
    if (streets.length < 2) return;

    const types = Array.from(info.types);
    let priority = 4;
    if (types.some(t => t === 'motorway' || t === 'trunk')) priority = 1;
    else if (types.some(t => t === 'primary')) priority = 2;
    else if (types.some(t => t === 'secondary' || t === 'tertiary')) priority = 3;

    const dir = getCardinalDirection(lat, lng, info.lat, info.lng);

    closures.push({
      id: nid,
      name: streets.slice(0, 3).join(' & '),
      streets,
      lat: info.lat,
      lng: info.lng,
      distance: info.dist,
      priority,
      roadType: types[0] || 'local',
      direction: dir,
      bearing: Math.atan2(info.lng - lng, info.lat - lat) * 180 / Math.PI,
    });
  });

  // Dedup: remove closures within ~0.03 mi of each other, keep higher priority
  closures.sort((a, b) => a.priority - b.priority);
  const deduped: PerimeterClosure[] = [];
  closures.forEach(c => {
    if (!deduped.some(d => haversine(c.lat, c.lng, d.lat, d.lng) < 0.03)) {
      deduped.push(c);
    }
  });

  // Sort by cardinal direction
  deduped.sort((a, b) => DIR_ORDER.indexOf(a.direction as typeof DIR_ORDER[number]) - DIR_ORDER.indexOf(b.direction as typeof DIR_ORDER[number]));

  return deduped;
}

// ── Reverse Geocode (HERE) ───────────────────────────────────

export async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const params = new URLSearchParams({ at: `${lat},${lng}` });
    const r = await fetch(`${WORKER}/api/here/revgeocode?${params}`, { headers: H });
    const d = await r.json();
    const items = (d as { items?: HEREGeocodeItem[] }).items;
    if (items?.[0]?.address?.label) return items[0].address.label;
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  } catch {
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }
}

// ── Search Address (HERE Geocode) ────────────────────────────

export interface AddressResult {
  lat: string;
  lon: string;
  display_name: string;
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  boundingbox: string[];
  class: string;
  type: string;
  importance: number;
}

export async function searchAddress(query: string): Promise<AddressResult[]> {
  const params = new URLSearchParams({
    q: query,
    in: 'countryCode:USA',
    limit: '5',
  });
  const r = await fetch(`${WORKER}/api/here/geocode?${params}`, { headers: H });
  if (!r.ok) throw new Error(`Geocode failed: ${r.status}`);
  const d = await r.json();
  const items = (d as { items?: HEREGeocodeItem[] }).items || [];

  // Map HERE results to the AddressResult interface for backwards compat
  return items.map((item, i) => ({
    lat: String(item.position.lat),
    lon: String(item.position.lng),
    display_name: item.address?.label || item.title,
    place_id: i,
    licence: 'HERE',
    osm_type: '',
    osm_id: 0,
    boundingbox: [],
    class: '',
    type: '',
    importance: item.scoring?.queryScore ?? 0.5,
  }));
}
